import { useCallback, useEffect, useRef, useState } from 'react';
import { createAssistantChime } from './assistantChime';

export const WELCOME_KEY = 'bezalel-assistant-welcome-seen';
const MUTE_KEY = 'bezalel-assistant-muted';
const ORBIT_KEY = 'bezalel-assistant-orbit-paused';
const read = key => { try { return sessionStorage.getItem(key) === '1'; } catch { return false; } };
const save = (key, value) => { try { sessionStorage.setItem(key, value ? '1' : '0'); } catch { /* Private browsing may disable storage. */ } };

export default function useAssistantWelcome(open) {
  const [visible, setVisible] = useState(false);
  const [muted, setMuted] = useState(() => read(MUTE_KEY));
  const [orbitPaused, setOrbitPaused] = useState(() => read(ORBIT_KEY));
  const seen = useRef(read(WELCOME_KEY));
  const sound = useRef(null);
  const muteRef = useRef(muted);
  const dismiss = useCallback(() => {
    seen.current = true; save(WELCOME_KEY, true); setVisible(false);
  }, []);
  const toggleMuted = () => {
    const value = !muteRef.current;
    muteRef.current = value; save(MUTE_KEY, value); setMuted(value);
  };
  const toggleOrbit = () => setOrbitPaused(value => { save(ORBIT_KEY, !value); return !value; });

  useEffect(() => {
    sound.current = createAssistantChime();
    const unlock = event => { if (event.isTrusted && !muteRef.current && !seen.current) sound.current?.unlock(); };
    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock); window.removeEventListener('keydown', unlock);
      sound.current?.dispose(); sound.current = null;
    };
  }, []);

  useEffect(() => {
    if (open) { dismiss(); return undefined; }
    if (seen.current) return undefined;
    let remaining = 5000, started = null, timer;
    const schedule = () => {
      clearTimeout(timer);
      if (started !== null) remaining = Math.max(0, remaining - (Date.now() - started));
      started = null;
      if (document.visibilityState === 'hidden') return;
      started = Date.now();
      timer = setTimeout(() => {
        if (seen.current) return;
        seen.current = true; save(WELCOME_KEY, true); setVisible(true);
        if (!muteRef.current) sound.current?.play();
      }, remaining);
    };
    schedule();
    document.addEventListener('visibilitychange', schedule);
    return () => { clearTimeout(timer); document.removeEventListener('visibilitychange', schedule); };
  }, [open, dismiss]);

  return { visible, dismiss, muted, toggleMuted, orbitPaused, toggleOrbit };
}
